/**
 * طبقة الاتصال لشاشة «الليدز» — طلبات التسجيل (design/Leads.dc.html).
 *
 * النقط (docs/03 §2): `GET /leads` · `GET /leads/funnel` ·
 * `POST /leads/{id}/{claim|contact|nurture}` · `POST /leads/bulk/{reassign|remind}` ·
 * `GET /admin/portal/status`.
 *
 * 🔴 بلاغان مؤكدان اتصلحوا هنا مش في العرض بس:
 *   1) **العنوان كان بيكدب**: مكتوب «الطلبات المفتوحة» والمعروض كل الطلبات
 *      بما فيها المقفولة ⇒ عنوان الجدول بقى تابع للفلتر النشط، ومجموع
 *      الفلاتر بيتراجع مع «الكل» وأي اختلاف بيتسجّل وبيتقال للمستخدم.
 *   2) **الترتيب كان بيكدب**: مكتوب «مرتّب بالأقدم انتظارًا» والصفوف مش
 *      مرتّبة ⇒ المسؤولة بتكلّم الأحدث وتسيب اللي مستني من 12 يوم.
 *      دلوقتي بنطلب `sort=waiting_since`، وبنعيد الترتيب في العرض،
 *      **ولو رد السيستم جه بترتيب تاني الجملة نفسها بتتغيّر** بدل ما
 *      نفضل مدّعيين ترتيب مش موجود.
 *
 * ⛔ ممنوع «نجاح» على رد فاضي · ⛔ ممنوع الفشل الصامت.
 */
import { api, ApiError } from "@/api/client";

/* ══════════════ أنواع ══════════════ */

export type LeadSegment = "all" | "needs_followup" | "on_track" | "closed";

export type LeadTone = "late" | "soon" | "okc" | "info" | "ghost";

export type LeadAction = {
  key: string;
  label_ar: string;
  href: string | null;
};

export type LeadRow = {
  id: string;
  display_name_ar: string;
  phone_masked: string;
  language: string;
  source: string;
  waiting_since: string | null;
  waiting_days: number | null;
  /** الأسرة بعتت الفورم تاني وهي مستنية — إشارة استعجال */
  resubmit_count: number | null;
  status: string;
  status_label_ar: string;
  assignee_name_ar: string | null;
  next_action: LeadAction | null;
  version: number;
};

export type LeadsPage = {
  rows: LeadRow[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  /** عدّاد لكل فلتر — `null` يعني السيستم ما بعتوش (مش صفر) */
  counts: Record<LeadSegment, number | null>;
  /** مجموع التلات شرايح = «الكل»؟ `null` لما العدّادات ناقصة */
  countsConsistent: boolean | null;
  /** الترتيب المعروض فعلًا هو اللي الشاشة طلبته؟ */
  sortTrusted: boolean;
  /**
   * 🔑 بلاغ الفريق: «الأسر الجديدة تكون في أول صفحة مش آخر صفحة».
   *    «الكل» والمقفولة بالأحدث الأول — سجل بتدوّري فيه على اللي لسه واصل.
   *    «محتاجة متابعة» بتفضل بالأقدم انتظارًا — طابور، واللي مستني من
   *    أسبوعين هو اللي بيضيع.
   */
  newestFirst: boolean;
};

/** الترتيب اللي الشاشة بتطلبه لكل شريحة — قاعدة واحدة في مكان واحد */
export function sortForSegment(segment: LeadSegment): "waiting_since" | "-waiting_since" {
  return segment === "needs_followup" ? "waiting_since" : "-waiting_since";
}

export type Funnel = {
  arrived: number;
  contacted: number;
  evaluation_booked: number;
  completed: number;
  rates: { contacted: string; evaluation_booked: string; completed: string };
  biggestDropAr: string | null;
};

export type PortalStatus = {
  mode: string;
  messageAr: string;
  lastSyncAt: string | null;
};

/* ══════════════ الشرايح والعناوين ══════════════ */

/**
 * ⚠️ العنوان تابع للفلتر — مش نص ثابت. الخريطة نفسها في docs/03 §2.
 *
 * 🔑 **«مستني تواصل» مش «محتاج متابعة».** الاسم القديم كان مطابقًا بالحرف
 *    لشريحة شاشة الإشراف الإداري، وهما حاجتان مختلفتان تمامًا: دي طلب لسه
 *    في مرحلة المكالمات، وديك توقف موعد متابعته عدّى. ومعجم الليدز في
 *    الكود كله «تواصل» و«انتظار» («جديد — متأخر التواصل» · «تم التواصل» ·
 *    «الأقدم انتظارًا») — «متابعة» كانت كلمة مستعارة مالهاش جذر هنا.
 *
 * ⛔ المفتاح `needs_followup` **قيمة على السلك** — ما يتغيّرش. تغييره بيفشل
 *    **بصمت**: `segment_query` في الباك إند بيرجّع «الكل» ويسجّل ERROR،
 *    فالشاشة تعرض 31 طلبًا والعدّاد جنبها 14 بلا أي رسالة خطأ.
 */
export const LEAD_SEGMENTS: {
  key: LeadSegment; chip: string; title: string; defAr?: string;
}[] = [
  { key: "all", chip: "الكل", title: "كل الطلبات" },
  {
    key: "needs_followup",
    chip: "مستني تواصل",
    title: "الطلبات اللي مستنية تواصل",
    defAr: "الطلب لسه في مرحلة المكالمات وما اتحجزلوش تقييم",
  },
  { key: "on_track", chip: "ماشي صح", title: "الطلبات اللي ماشية صح" },
  { key: "closed", chip: "مقفول", title: "الطلبات المقفولة" },
];

const SOURCE_LABELS: Record<string, string> = {
  website_form: "فورم الموقع",
  whatsapp: "واتساب",
  phone_call: "مكالمة",
  referral: "إحالة",
  manual: "إدخال يدوي",
};

const LANGUAGE_LABELS: Record<string, string> = {
  ar: "عربي",
  en: "إنجليزي",
  fr: "فرنسي",
};

export function sourceLabelAr(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

export function languageLabelAr(language: string): string {
  return LANGUAGE_LABELS[language] ?? language;
}

/** القناة دي مالهاش مدخل آلي — الفريق بيسجّلها بإيده */
export function isManualSource(source: string): boolean {
  return source === "whatsapp" || source === "manual" || source === "referral";
}

/** لون واحد لكل حالة (نقطة + لون) — نفس خريطة التصميم */
export function leadTone(row: LeadRow): LeadTone {
  switch (row.status) {
    case "converted":
      return "okc";
    case "contacted":
    case "evaluation_booked":
      return "info";
    case "postponed":
      return "soon";
    case "never_completed":
    case "lost":
      return "ghost";
    case "new":
      // جديد ولسه ماحدش كلّمه من يوم أو أكتر = متأخر فعلًا
      return (row.waiting_days ?? 0) >= 1 ? "late" : "soon";
    default:
      return "ghost";
  }
}

/* ══════════════ أدوات ══════════════ */

function fail(path: string, reason: string): never {
  console.error(`[leads] ${reason} (${path})`);
  throw new ApiError(
    502,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رجّع رد مش مفهوم — مقدرناش نعرض الطلبات" },
    null,
  );
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function newIdempotencyKey(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `rtl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeLead(raw: Record<string, unknown>, path: string): LeadRow {
  const id = str(raw["id"]);
  if (!id) fail(path, "طلب بلا معرّف في الرد");
  const assignee = (raw["assignee"] ?? null) as Record<string, unknown> | null;
  const action = (raw["next_action"] ?? null) as Record<string, unknown> | null;
  return {
    id,
    display_name_ar: str(raw["display_name_ar"]) || "مجهول الاسم",
    phone_masked: str(raw["phone_masked"]),
    language: str(raw["language"]),
    source: str(raw["source"]),
    waiting_since: strOrNull(raw["waiting_since"]),
    waiting_days: numOrNull(raw["waiting_days"]),
    resubmit_count: numOrNull(raw["resubmit_count"]),
    status: str(raw["status"]),
    status_label_ar: str(raw["status_label_ar"]) || str(raw["status"]),
    assignee_name_ar: assignee ? strOrNull(assignee["name_ar"]) : null,
    next_action: action
      ? {
          key: str(action["key"]),
          label_ar: str(action["label_ar"]),
          href: strOrNull(action["href"]),
        }
      : null,
    version: typeof raw["version"] === "number" ? raw["version"] : 1,
  };
}

/** ثواني الانتظار — الأقدم أولًا. الصف بلا تاريخ بيروح الآخر مش الأول */
function waitKey(row: LeadRow): number {
  if (!row.waiting_since) return Number.POSITIVE_INFINITY;
  const t = new Date(row.waiting_since).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/* ══════════════ القراءة ══════════════ */

export async function fetchLeads(input: {
  segment: LeadSegment;
  page: number;
  perPage: number;
}): Promise<LeadsPage> {
  const path = "/leads";
  const sort = sortForSegment(input.segment);
  const newestFirst = sort === "-waiting_since";
  const res = await api.get<unknown>(path, {
    query: {
      segment: input.segment,
      sort,
      page: input.page,
      per_page: input.perPage,
      with_counts: true,
    },
  });
  if (!Array.isArray(res.data)) fail(path, "الرد مش قائمة");
  const rows = (res.data as Record<string, unknown>[]).map((raw) => normalizeLead(raw, path));

  // الترتيب المعلن حسب الشريحة. بنرتّب في العرض كمان، ولو رد السيستم كان
  // بترتيب تاني بنسجّل ERROR ونبطّل ندّعي الجملة.
  const sorted = [...rows].sort((a, b) =>
    newestFirst ? waitKey(b) - waitKey(a) : waitKey(a) - waitKey(b),
  );
  const serverSaysWaiting = str(res.meta?.sort) === sort;
  const sameOrder = rows.every((row, i) => sorted[i]?.id === row.id);
  if (!sameOrder) {
    console.error(
      `[leads] الصفحة رجعت بترتيب غير «${newestFirst ? "الأحدث" : "الأقدم انتظارًا"}»` +
        ` (sort=${str(res.meta?.sort) || "غير معروف"}) —` +
        " اترتّبت في العرض، لكن الصفحات التانية ممكن تكون ناقصة",
    );
  }

  const raw = res.counts ?? null;
  const counts: Record<LeadSegment, number | null> = {
    all: numOrNull(raw?.["all"]),
    needs_followup: numOrNull(raw?.["needs_followup"]),
    on_track: numOrNull(raw?.["on_track"]),
    closed: numOrNull(raw?.["closed"]),
  };
  let countsConsistent: boolean | null = null;
  if (
    counts.all !== null &&
    counts.needs_followup !== null &&
    counts.on_track !== null &&
    counts.closed !== null
  ) {
    const sum = counts.needs_followup + counts.on_track + counts.closed;
    countsConsistent = sum === counts.all;
    if (!countsConsistent) {
      // ⚠️ قاعدة مقفولة: مجموع الفلاتر = «الكل». الاختلاف عطل بيتقال مش بيتخبّى.
      console.error(`[leads] مجموع الفلاتر (${sum}) مش مساوي «الكل» (${counts.all})`);
    }
  }

  const total = numOrNull(res.meta?.total) ?? rows.length;
  const perPage = numOrNull(res.meta?.per_page) ?? input.perPage;
  return {
    rows: sorted,
    page: numOrNull(res.meta?.page) ?? input.page,
    perPage,
    total,
    totalPages: numOrNull(res.meta?.total_pages) ?? Math.max(1, Math.ceil(total / perPage)),
    counts,
    countsConsistent,
    sortTrusted: serverSaysWaiting && sameOrder,
    newestFirst,
  };
}

export async function fetchFunnel(period: "week" | "day"): Promise<Funnel> {
  const path = "/leads/funnel";
  const res = await api.get<unknown>(path, { query: { period } });
  const d = res.data as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "قمع فاضي في الرد");
  const rates = (d["rates"] ?? {}) as Record<string, unknown>;
  const drop = (d["biggest_drop"] ?? null) as Record<string, unknown> | null;
  return {
    arrived: numOrNull(d["arrived"]) ?? 0,
    contacted: numOrNull(d["contacted"]) ?? 0,
    evaluation_booked: numOrNull(d["evaluation_booked"]) ?? 0,
    completed: numOrNull(d["completed"]) ?? 0,
    rates: {
      contacted: str(rates["contacted"]),
      evaluation_booked: str(rates["evaluation_booked"]),
      completed: str(rates["completed"]),
    },
    biggestDropAr: drop ? strOrNull(drop["label_ar"]) : null,
  };
}

export async function fetchPortalStatus(): Promise<PortalStatus> {
  const path = "/admin/portal/status";
  const res = await api.get<unknown>(path);
  const d = res.data as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "حالة الربط رجعت فاضية");
  return {
    mode: str(d["mode"]),
    messageAr: str(d["message_ar"]),
    lastSyncAt: strOrNull(d["last_sync_at"]),
  };
}

/* ══════════════ الأفعال ══════════════ */

/** فعل الصف اللي الواجهة تقدر تنفّذه من الجدول بلا بيانات زيادة */
const INLINE_ACTIONS = new Set(["claim", "contact", "nurture"]);

const ACTION_TOASTS: Record<string, string> = {
  claim: "اتسلّمت — بقت مسؤوليتك",
  contact: "اتسجّل إنك كلّمتهم",
  nurture: "رسالة الرعاية اتسجّلت",
};

export type ActionOutcome = {
  toastAr: string;
  /** لو الفعل فتح رحلة — الشاشة بتوجّه لها */
  href: string | null;
};

/** فعل محتاج شاشة تانية (تحويل · تأجيل · عرض باقات) — بنقول كده صريح */
export class ActionNotHereError extends Error {
  readonly messageAr: string;
  constructor(labelAr: string) {
    super("action not available inline");
    this.name = "ActionNotHereError";
    this.messageAr = `«${labelAr}» مش بيتعمل من الجدول — افتح ملف الطلب`;
  }
}

/** نتايج إقفال الطلب — **`converted` مش منهم**: التحويل بيحصل من مساره،
 *  وإعلانه يدويًا بيعمل طلبًا «متحوّل» بلا أسرة. */
export const CLOSE_OUTCOMES = [
  { key: "postponed", labelAr: "مؤجَّل — هيرجع",
    hintAr: "الرحلة بتتعلّق ومهامها بتقف — بترجع لما يرجع" },
  { key: "never_completed", labelAr: "ما كمّلش التسجيل",
    hintAr: "الرحلة بتتقفل ومهامها بتتلغي" },
  { key: "lost", labelAr: "مقفول — مش هيكمل",
    hintAr: "الرحلة بتتقفل ومهامها بتتلغي" },
] as const;

export type CloseOutcome = (typeof CLOSE_OUTCOMES)[number]["key"];

/** إقفال طلب مش هيكمل — **ومعاه رحلته**.
 *
 * 🔴 من غيره الطلب بيفضل في طابور الفريق للأبد، ورحلته بتفضل تولّد مهام
 *    ومهلها بتتأخر — لطلب الفريق عارف إنه خلاص.
 * ⚠️ السبب **إجباري** (٣ حروف على الأقل): الطلب المقفول بلا سبب محدش بيراجعه.
 */
export async function closeLead(
  leadId: string,
  outcome: CloseOutcome,
  reasonAr: string,
): Promise<ActionOutcome> {
  const path = `/leads/${leadId}/close`;
  const res = await api.post<unknown>(path, { outcome, reason_ar: reasonAr });
  const d = (res.data ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "الإقفال رجّع رد فاضي");
  return { toastAr: str(d["toast_ar"]) || "الطلب اتقفل", href: null };
}

export async function runLeadAction(row: LeadRow): Promise<ActionOutcome> {
  const action = row.next_action;
  if (!action || !action.key) {
    throw new ActionNotHereError("الإجراء");
  }
  if (!INLINE_ACTIONS.has(action.key)) {
    // الرابط موجود ⇒ بنفتحه بدل ما نعمل حاجة نص نص
    if (action.href) return { toastAr: "", href: action.href };
    throw new ActionNotHereError(action.label_ar || action.key);
  }
  const path = `/leads/${row.id}/${action.key}`;
  const res = await api.post<unknown>(
    path,
    undefined,
    action.key === "claim" ? { idempotencyKey: newIdempotencyKey() } : {},
  );
  const d = (res.data ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "الفعل رجّع رد فاضي");
  const journeyId = strOrNull(d["journey_id"]);
  return {
    toastAr: str(d["toast_ar"]) || ACTION_TOASTS[action.key] || "اتسجّل",
    href: journeyId ? `/journeys/${journeyId}` : null,
  };
}

/** حد المواصفة: 50 عنصر كحد أقصى للطلب الجماعي الواحد */
export const BULK_LIMIT = 50;

function guardBulk(ids: string[]): void {
  if (ids.length === 0) throw new ApiError(400, { code: "EMPTY_SELECTION", message_ar: "محدش متحدّد" }, null);
  if (ids.length > BULK_LIMIT) {
    throw new ApiError(
      400,
      { code: "BULK_TOO_LARGE", message_ar: `الحد ${BULK_LIMIT} طلب في المرة — قلّل التحديد` },
      null,
    );
  }
}

export async function bulkReassign(
  leadIds: string[],
  toUserId: string,
  reasonAr: string,
): Promise<number> {
  guardBulk(leadIds);
  const path = "/leads/bulk/reassign";
  const res = await api.post<unknown>(path, {
    lead_ids: leadIds,
    to_user_id: toUserId,
    reason_ar: reasonAr,
  });
  const d = (res.data ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "إعادة التوزيع الجماعية رجّعت رد فاضي");
  return numOrNull(d["updated"]) ?? leadIds.length;
}

export async function bulkRemind(leadIds: string[], channel: string): Promise<number> {
  guardBulk(leadIds);
  const path = "/leads/bulk/remind";
  const res = await api.post<unknown>(path, { lead_ids: leadIds, channel });
  const d = (res.data ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "التذكير الجماعي رجّع رد فاضي");
  return numOrNull(d["sent"]) ?? leadIds.length;
}

/** «إضافة طلب يدوي» — الطلب اللي جه واتساب أو بمكالمة يدخل السيستم.
 *
 * ⭐ **ليه المسار ده موجود:** ربط الواتساب المباشر محتاج API رسمي مكلّف،
 *    والطريق غير الرسمي معرّض إن رقم الأكاديمية يتوقف. فالإدخال يدوي —
 *    بس **جوّه السيستم**، مش في نوتة ولا في محادثة واتساب.
 *
 * 🔑 الرد بيفرّق بين «اتسجّل» و«فيه واحد بنفس الرقم». التاني **مش فشل**:
 *    الطلب القديم موجود والعدّاد زاد، والموظفة محتاجة تعرف كده عشان
 *    تفتح القديم بدل ما تحاول تاني وتفتكر إن إدخالها ضاع.
 */
export type ManualLeadInput = {
  studentName: string;
  phone: string;
  source: "whatsapp" | "phone_call" | "referral" | "manual";
  entryNoteAr?: string;
  lang?: "ar" | "en" | "fr";
};

export async function createManualLead(
  input: ManualLeadInput,
): Promise<{ toastAr: string; duplicate: boolean; row: LeadRow }> {
  const path = "/leads";
  const res = await api.post<unknown>(path, {
    student_name: input.studentName.trim(),
    phone: input.phone.trim(),
    source: input.source,
    ...(input.entryNoteAr?.trim() ? { entry_note_ar: input.entryNoteAr.trim() } : {}),
    ...(input.lang ? { lang: input.lang } : {}),
  });
  const d = (res.data ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "الإضافة رجّعت رد فاضي");
  return {
    toastAr: str(d["toast_ar"]) || "الطلب اتسجّل",
    duplicate: d["duplicate"] === true,
    row: normalizeLead(d, path),
  };
}

/* ══════════════ تحويل الطلب لأسرة وطلاب ══════════════
   🔴 الخطوة دي كانت موجودة في الخادم بس (١٤ أكتوبر) — الشاشة ما كانش فيها
   أي زرار ليها، فالفريق وقف عند «حجز التقييم» وقال «أعمل الأسرة منين؟». */

export type ConvertPreview = {
  familyNameAr: string;
  guardianNameAr: string | null;
  countryRaw: string | null;
  country: string | null;
  timezone: string | null;
  students: { nameAr: string; age: number | null }[];
  flags: string[];
};

export async function fetchConvertPreview(leadId: string): Promise<ConvertPreview> {
  const path = `/leads/${leadId}/convert/preview`;
  const res = await api.get<unknown>(path);
  const d = (res.data ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "المعاينة رجعت رد فاضي");
  const fam = (d["family"] ?? {}) as Record<string, unknown>;
  const students = Array.isArray(d["students"]) ? (d["students"] as Record<string, unknown>[]) : [];
  return {
    familyNameAr: str(fam["name_ar"]),
    guardianNameAr: strOrNull(fam["guardian_name_ar"]),
    countryRaw: strOrNull(fam["country_raw"]) ?? strOrNull(fam["country"]),
    country: strOrNull(fam["country"]),
    timezone: strOrNull(fam["timezone"]),
    students: students.map((s) => ({
      nameAr: str(s["name_ar"]),
      age: typeof s["age_years"] === "number" ? (s["age_years"] as number) : null,
    })),
    flags: Array.isArray(d["will_be_flagged"])
      ? (d["will_be_flagged"] as Record<string, unknown>[]).map((f) => str(f["label_ar"]) || str(f["key"]))
      : [],
  };
}

export async function convertLead(
  leadId: string,
  input: { familyNameAr: string; timezone: string; country: string; children: { nameAr: string; age: number | null }[] },
): Promise<{ toastAr: string; journeyId: string | null; familyCode: string }> {
  const path = `/leads/${leadId}/convert`;
  const res = await api.post<unknown>(path, {
    family_name_ar: input.familyNameAr.trim(),
    timezone: input.timezone,
    country: input.country,
    children: input.children.map((c) => ({ name_ar: c.nameAr.trim(), ...(c.age ? { age: c.age } : {}) })),
  });
  const d = (res.data ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "التحويل رجّع رد فاضي");
  return {
    toastAr: str(d["toast_ar"]) || "اتحوّل لأسرة",
    journeyId: strOrNull(d["journey_id"]),
    familyCode: str(d["family_code"]),
  };
}

/** المناطق الزمنية اللي أسر الأكاديمية فيها فعلًا — الأكتر أولًا. */
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: "Africa/Cairo", label: "مصر (القاهرة)" },
  { value: "Asia/Riyadh", label: "السعودية · الكويت · قطر · البحرين" },
  { value: "Asia/Dubai", label: "الإمارات · عُمان" },
  { value: "Asia/Amman", label: "الأردن · فلسطين" },
  { value: "Europe/Istanbul", label: "تركيا" },
  { value: "Europe/London", label: "بريطانيا · أيرلندا" },
  { value: "Europe/Paris", label: "فرنسا · ألمانيا · هولندا · بلجيكا · إيطاليا · إسبانيا" },
  { value: "Europe/Stockholm", label: "السويد · النرويج · الدنمارك" },
  { value: "America/New_York", label: "أمريكا الشرقية · كندا (تورونتو)" },
  { value: "America/Chicago", label: "أمريكا الوسطى (شيكاغو · تكساس)" },
  { value: "America/Denver", label: "أمريكا الجبلية" },
  { value: "America/Los_Angeles", label: "أمريكا الغربية · كندا (فانكوفر)" },
  { value: "Australia/Sydney", label: "أستراليا (سيدني)" },
  { value: "Asia/Kuala_Lumpur", label: "ماليزيا" },
  { value: "Asia/Jakarta", label: "إندونيسيا" },
  { value: "Africa/Casablanca", label: "المغرب" },
  { value: "Africa/Algiers", label: "الجزائر · تونس" },
];

/** أسماء المصادر اللي الموظف يختار منها — نفس القيم المسموحة في السيرفر. */
export const MANUAL_SOURCES: { key: ManualLeadInput["source"]; label: string }[] = [
  { key: "whatsapp", label: "واتساب" },
  { key: "phone_call", label: "مكالمة" },
  { key: "referral", label: "إحالة من حد" },
  { key: "manual", label: "حاجة تانية" },
];
